import { RefObject, KeyboardEvent } from 'react';

interface IUseTrapProps {
  /** the element you wish to keep focus on */
  ref: RefObject<HTMLElement>;
  /** toggle active state */
  active: boolean;
}

const focusableElements = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type=hidden])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'object',
  'embed',
  '[tabindex]:not(.modal)',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
];

const getFocusableChildren = (el: HTMLElement) =>
  el.querySelectorAll(focusableElements.join(', ')) as unknown as HTMLCollectionOf<HTMLElement>;

const getFocusedChild = (focusableChildren: HTMLCollectionOf<HTMLElement>) => {
  let currentFocus;
  try {
    currentFocus = document.activeElement;
  } catch (err) {
    /* istanbul ignore next */
    [currentFocus] = focusableChildren;
  }
  return currentFocus;
};

const useTrapFocus =
  ({ active, ref }: IUseTrapProps) =>
  (e: KeyboardEvent) => {
    if (e.which !== 9 || !ref.current || !active) return;
    const focusableChildren = getFocusableChildren(ref.current);
    const totalFocusable = focusableChildren.length;
    if (totalFocusable === 0) return;
    const currentFocus = getFocusedChild(focusableChildren);

    let focusedIndex = 0;

    for (let i = 0; i < totalFocusable; i += 1) {
      if (focusableChildren[i] === currentFocus) {
        focusedIndex = i;
        break;
      }
    }

    if (e.shiftKey && focusedIndex === 0) {
      e.preventDefault();
      focusableChildren[totalFocusable - 1].focus();
    } else if (!e.shiftKey && focusedIndex === totalFocusable - 1) {
      e.preventDefault();
      focusableChildren[0].focus();
    }
  };

export default useTrapFocus;
